import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Download = () => {
  const [release, setRelease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLatestRelease = async () => {
      try {
        const response = await fetch('https://api.github.com/repos/ShubhamJR8/cricket-content-editor/releases/latest');
        if (!response.ok) {
          throw new Error('Failed to fetch latest release data');
        }
        const data = await response.json();
        
        const macAsset = data.assets.find(a => a.name.endsWith('.dmg'));
        const winAsset = data.assets.find(a => a.name.endsWith('.exe'));
        
        setRelease({
          version: data.name || data.tag_name,
          macUrl: macAsset ? macAsset.browser_download_url : null,
          winUrl: winAsset ? winAsset.browser_download_url : null,
          publishedAt: new Date(data.published_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }),
          notes: data.body
        });
      } catch (err) {
        console.error('Error fetching release:', err);
        setError('Could not load the latest version. Please check back later.');
      } finally {
        setLoading(false);
      }
    };

    fetchLatestRelease();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen mesh-gradient grain-texture pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-pitch-green/10 rounded-full blur-[120px] -z-10 animate-pulse"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px] -z-10 animate-pulse" style={{ animationDelay: '2s' }}></div>

      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center mb-16"
        >
          <motion.span 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-block px-4 py-1.5 mb-6 text-xs font-bold tracking-widest text-pitch-green uppercase bg-pitch-green/10 border border-pitch-green/20 rounded-full"
          >
            Now in Beta
          </motion.span>
          <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 tracking-tight">
            Elevate Your <span className="text-pitch-green text-glow">Cricket Content</span>
          </h1>
          <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto leading-relaxed">
            Professional-grade video editing suite tailored for cricket enthusiasts. 
            Automate your workflow and create stunning reels in minutes.
          </p>
        </motion.div>

        {loading ? (
          <div className="flex flex-col justify-center items-center py-20 space-y-4">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 border-4 border-pitch-green/20 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-pitch-green border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-pitch-green font-medium animate-pulse">Fetching latest build...</p>
          </div>
        ) : error ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card border-red-500/20 rounded-2xl p-8 text-center max-w-xl mx-auto"
          >
            <div className="text-red-400 mb-4 text-5xl">⚠️</div>
            <p className="text-red-300 font-medium">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-6 px-6 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-200 rounded-full transition-all border border-red-500/30"
            >
              Try Again
            </button>
          </motion.div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-12"
          >
            {/* Main Download Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Windows Download */}
              <motion.div
                variants={itemVariants}
                whileHover={{ y: -5 }}
                className="glass-card glass-card-hover rounded-3xl p-8 flex flex-col items-center text-center relative overflow-hidden group"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full -mr-16 -mt-16 blur-2xl group-hover:bg-blue-500/10 transition-colors"></div>
                
                <div className="w-20 h-20 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-6 border border-blue-500/20 group-hover:scale-110 transition-transform duration-500">
                  <svg className="w-10 h-10 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/>
                  </svg>
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-2">Windows</h3>
                <p className="text-gray-400 text-sm mb-8">x64 architecture (Windows 10+)</p>

                {release.winUrl ? (
                  <a
                    href={release.winUrl}
                    className="w-full relative group/btn"
                  >
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-pitch-green rounded-xl blur opacity-30 group-hover/btn:opacity-60 transition duration-1000 group-hover/btn:duration-200"></div>
                    <div className="relative flex items-center justify-center w-full bg-stadium-black hover:bg-gray-900 text-white font-bold py-4 px-6 rounded-xl transition-all border border-white/10">
                      <svg className="w-5 h-5 mr-3 text-pitch-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                      </svg>
                      Download .exe
                    </div>
                  </a>
                ) : (
                  <div className="w-full py-4 px-6 bg-white/5 text-gray-500 font-bold rounded-xl border border-white/5 cursor-not-allowed">
                    Coming Soon
                  </div>
                )}
              </motion.div>

              {/* Mac Download */}
              <motion.div
                variants={itemVariants}
                whileHover={{ y: -5 }}
                className="glass-card glass-card-hover rounded-3xl p-8 flex flex-col items-center text-center relative overflow-hidden group"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-pitch-green/5 rounded-full -mr-16 -mt-16 blur-2xl group-hover:bg-pitch-green/10 transition-colors"></div>

                <div className="w-20 h-20 bg-white/5 rounded-2xl flex items-center justify-center mb-6 border border-white/10 group-hover:scale-110 transition-transform duration-500">
                  <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.057 14.853c.036 2.373 2.062 3.161 2.094 3.174-.018.062-.328 1.125-1.078 2.224-.65 0.947-1.322 1.888-2.38 1.908-1.039.02-1.375-.615-2.568-.615-1.192 0-1.564.603-2.546.634-1.018.031-1.785-.989-2.44-1.936-1.341-1.937-2.366-5.474-1.025-7.8c.665-1.154 1.854-1.887 3.136-1.905 0.977-.02 1.898.653 2.496.653 0.598 0 1.705-.815 2.87-.694 0.49.02 1.862.197 2.744 1.488-0.071.044-1.637.954-1.618 2.836l-0.024 0.01zM14.939 5.894c0.518-0.627 0.868-1.498 0.772-2.369-0.748.03-1.654.5-2.191 1.127-0.482.559-0.904 1.449-0.792 2.302 0.83.064 1.688-0.428 2.211-1.06z"/>
                  </svg>
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-2">macOS</h3>
                <p className="text-gray-400 text-sm mb-8">Apple Silicon & Intel (11.0+)</p>

                {release.macUrl ? (
                  <a
                    href={release.macUrl}
                    className="w-full relative group/btn"
                  >
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-pitch-green to-blue-500 rounded-xl blur opacity-30 group-hover/btn:opacity-60 transition duration-1000 group-hover/btn:duration-200"></div>
                    <div className="relative flex items-center justify-center w-full bg-stadium-black hover:bg-gray-900 text-white font-bold py-4 px-6 rounded-xl transition-all border border-white/10">
                      <svg className="w-5 h-5 mr-3 text-pitch-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                      </svg>
                      Download .dmg
                    </div>
                  </a>
                ) : (
                  <div className="w-full py-4 px-6 bg-white/5 text-gray-500 font-bold rounded-xl border border-white/5 cursor-not-allowed">
                    Coming Soon
                  </div>
                )}
              </motion.div>
            </div>

            {/* Version Info & Features */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
              {/* Release Details */}
              <motion.div variants={itemVariants} className="lg:col-span-2 space-y-8">
                <div className="glass-card rounded-3xl p-8">
                  <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                    <div>
                      <h4 className="text-xl font-bold text-white flex items-center">
                        <span className="w-2 h-2 bg-pitch-green rounded-full mr-3 animate-pulse"></span>
                        Version {release.version}
                      </h4>
                      <p className="text-sm text-gray-400 mt-1">Released on {release.publishedAt}</p>
                    </div>
                    <div className="flex gap-2">
                      <span className="px-3 py-1 bg-white/5 border border-white/10 rounded-lg text-xs font-medium text-gray-300">Stable</span>
                      <span className="px-3 py-1 bg-white/5 border border-white/10 rounded-lg text-xs font-medium text-gray-300">LTS</span>
                    </div>
                  </div>

                  {release.notes && (
                    <div className="space-y-4">
                      <h5 className="text-sm font-bold text-pitch-green uppercase tracking-wider">What's New</h5>
                      <div className="bg-stadium-black/50 rounded-2xl p-6 border border-white/5 max-h-96 overflow-y-auto custom-scrollbar">
                        <div className="prose prose-invert prose-sm max-w-none text-gray-300">
                          <pre className="whitespace-pre-wrap font-sans leading-relaxed text-base">{release.notes}</pre>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* System Requirements */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="glass-card rounded-2xl p-6">
                    <h5 className="text-white font-bold mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-pitch-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"/>
                      </svg>
                      Hardware
                    </h5>
                    <ul className="space-y-2 text-sm text-gray-400">
                      <li className="flex justify-between"><span>RAM</span><span className="text-gray-200">8GB Minimum</span></li>
                      <li className="flex justify-between"><span>Storage</span><span className="text-gray-200">500MB Free</span></li>
                      <li className="flex justify-between"><span>GPU</span><span className="text-gray-200">Hardware Accel.</span></li>
                    </ul>
                  </div>
                  <div className="glass-card rounded-2xl p-6">
                    <h5 className="text-white font-bold mb-4 flex items-center">
                      <svg className="w-5 h-5 mr-2 text-pitch-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"/>
                      </svg>
                      Software
                    </h5>
                    <ul className="space-y-2 text-sm text-gray-400">
                      <li className="flex justify-between"><span>Windows</span><span className="text-gray-200">Version 10+</span></li>
                      <li className="flex justify-between"><span>macOS</span><span className="text-gray-200">Big Sur+</span></li>
                      <li className="flex justify-between"><span>Dependencies</span><span className="text-gray-200">Included</span></li>
                    </ul>
                  </div>
                </div>
              </motion.div>

              {/* Quick Features sidebar */}
              <motion.div variants={itemVariants} className="space-y-6">
                <div className="glass-card rounded-3xl p-8 bg-gradient-to-br from-pitch-green/5 to-transparent">
                  <h4 className="text-xl font-bold text-white mb-6">Why Deskstop?</h4>
                  <div className="space-y-6">
                    {[
                      { title: 'Local Processing', desc: 'Process videos locally without uploading large files.', icon: '⚡' },
                      { title: 'Frame Accuracy', desc: 'Precision clipping with sub-second control.', icon: '🎯' },
                      { title: 'Batch Export', desc: 'Export multiple reels simultaneously.', icon: '📦' },
                      { title: 'Zero Latency', desc: 'Instant playback and seeking.', icon: '🚀' }
                    ].map((feature, idx) => (
                      <div key={idx} className="flex gap-4">
                        <div className="text-2xl">{feature.icon}</div>
                        <div>
                          <h5 className="text-white font-semibold text-sm">{feature.title}</h5>
                          <p className="text-gray-400 text-xs mt-1">{feature.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="glass-card rounded-3xl p-8 border-pitch-green/20">
                  <h4 className="text-white font-bold mb-4">Need Help?</h4>
                  <p className="text-gray-400 text-sm mb-6">Join our community or check our documentation for installation guides.</p>
                  <a href="#" className="flex items-center justify-center w-full py-3 bg-white/5 hover:bg-white/10 text-white rounded-xl border border-white/10 transition-all text-sm font-medium">
                    View Documentation
                  </a>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      `}</style>
    </div>
  );
};

export default Download;
